/**
 * **How much** — base + rate, the one quantity rule (`builder-engine-and-
 * consistency.md` §6.8 item 1, board cb-19 cells 2–3; phone cb-22 cells 3–4).
 *
 * Props only. The same editor is the recipe line's popover section here and,
 * at step 10, the package builder's `Qty rule` cell. It shows a quantity (what
 * the rule makes at the sample measure, through `quantityFor`) and never a
 * price: money is the server's.
 */
import type { ReactNode } from 'react'
import { quantityFor } from '@rowted/shared'
import { formatNumber } from '../../lib/format.js'
import { FieldShell } from '../../components/fields.js'
import {
  AT_THE_SAMPLE, HOW_MUCH, HOW_MUCH_SUB, PER_OF, QTY_BASE_HINT, QTY_BASE_LABEL, QTY_RATE_HINT,
} from '../copy.js'
import { UnitInput } from './unit-input.js'
import { TiersEditor } from './tiers-editor.js'
import { readNumber, toTiers, tiersToBands, type DraftTier } from './tiers.js'

export interface QuantityRuleEditorProps {
  base: string
  rate: string
  onBase: (value: string) => void
  onRate: (value: string) => void
  /** `null` is a line with no tiers — the quiet row. */
  tiers: DraftTier[] | null
  onTiers: (tiers: DraftTier[] | null) => void
  /** What the line counts — `h`, `bag`, `trip`. */
  qtyUnit: string
  /** The measurement type's unit and name — `sq ft`, `Bed area` (§6.7 item 2). */
  measureUnit: string
  typeName: string
  /** The sheet's *Try it at* measure. */
  sample: number
  /**
   * In place of the tiers editor — the old Bands table, for a line whose bands
   * are not stacked (§6.8 item 5, D6).
   */
  tiersSlot?: ReactNode
}

export function QuantityRuleEditor({
  base, rate, onBase, onRate, tiers, onTiers, qtyUnit, measureUnit, typeName, sample, tiersSlot,
}: QuantityRuleEditorProps) {
  const own = { baseQty: readNumber(base), qtyPerUnit: readNumber(rate) }
  const made = quantityFor({ ...own, bands: [] }, sample)
  const tiered = tiers !== null && tiers.length > 1

  return (
    <section className="flex flex-col gap-3" aria-label={HOW_MUCH}>
      <div className="flex items-baseline gap-2">
        <b className="text-[14px] font-bold text-text">{HOW_MUCH}</b>
        <span className="text-[12px] leading-[1.35] text-text-muted">{HOW_MUCH_SUB}</span>
      </div>

      {tiered
        ? (
          /* With tiers the rate lives in the first tier's row (cb-19 · 3), so only the base stands here. */
          <div className="w-1/2 pr-5">
            <FieldShell label={QTY_BASE_LABEL} hint={QTY_BASE_HINT}>
              <UnitInput label={QTY_BASE_LABEL} value={base} onChange={onBase} unit={qtyUnit} />
            </FieldShell>
          </div>
        )
        : (
          <>
            <div className="grid grid-cols-[1fr_34px_1fr] items-end gap-x-1.5">
              <FieldShell label={QTY_BASE_LABEL} hint={QTY_BASE_HINT}>
                <UnitInput label={QTY_BASE_LABEL} value={base} onChange={onBase} unit={qtyUnit} />
              </FieldShell>
              <span className="mb-[22px] flex h-9 items-center justify-center text-[12.5px] text-text-faint">+</span>
              <FieldShell label={PER_OF(measureUnit, typeName)} hint={QTY_RATE_HINT}>
                <UnitInput label={PER_OF(measureUnit, typeName)} value={rate} onChange={onRate}
                  unit={`${qtyUnit} / ${measureUnit}`} />
              </FieldShell>
            </div>
            <p className="m-0 rounded-[10px] border border-line bg-surface-alt px-3 py-[9px] text-[12.5px] leading-[1.45] text-text-muted">
              <b className="font-semibold">
                {formatNumber(own.baseQty)} + {formatNumber(own.qtyPerUnit)} × {formatNumber(sample)} {measureUnit}
              </b>{' '}
              = {formatNumber(made)} {qtyUnit} {AT_THE_SAMPLE}
            </p>
          </>
        )}

      {tiersSlot ?? (
      <TiersEditor
        tiers={tiers}
        onChange={(next) => {
          onTiers(next)
          // The first tier's rate *is* the line's rate (§6.8 item 3).
          if (next && next[0] && next[0].qtyPerUnit !== rate) onRate(next[0].qtyPerUnit)
        }}
        rate={rate}
        qtyUnit={qtyUnit}
        measureUnit={measureUnit}
        sample={sample}
        quantityAt={(measure) => quantityFor({
          ...own,
          bands: tiers && tiers.length > 1 ? tiersToBands(own, toTiers(tiers)) : [],
        }, measure)}
      />
      )}
    </section>
  )
}
